import { EllipsisOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Descriptions,
  Dropdown,
  Menu,
  notification,
  Row,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { TableRowSelection } from 'antd/lib/table/interface';
import {
  useAddUsedResourcePricesMutation,
  useGetUsedResourcePricesQuery,
} from 'app/api';
import { StyledTable } from 'app/components/StyledTable/StyledTable';
import { useGlobalSlice } from 'app/containers/Global/slice';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { selectSelectedSeasonId } from 'app/containers/Season/slice/selectors';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toFixedIfFloat } from 'utils/helpers';
import {
  IUsedResourcePrice,
  IUsedResourcePriceCreateViewItem,
} from '../../slice/types';
import UsedResourcePriceDrawer from '../UsedResourcePriceDrawer/UsedResourcePriceDrawer';

interface IProps {
  usedResourceId: number;
}

const UsedResourceDetails: FC<IProps> = ({ usedResourceId }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const globalActions = useGlobalSlice().actions;

  const [isPriceDrawerOpen, setIsPriceDrawerOpen] = useState(false);
  const [selectedTableItems, setSelectedTableItems] = useState<
    IUsedResourcePrice[]
  >([]);

  const envId = useSelector(selectSelectedEnvId);
  const seasonId = useSelector(selectSelectedSeasonId);

  const [
    addUsedResourcePrice,
    { isLoading: isSubmitChangePriceLoading },
  ] = useAddUsedResourcePricesMutation();

  const {
    data: usedResourcePricesResponse,
    isFetching,
  } = useGetUsedResourcePricesQuery(
    {
      envId: envId as number,
      seasonId: seasonId as number,
      resourceId: usedResourceId,
    },
    { skip: !envId || !seasonId },
  );

  const usedResource = usedResourcePricesResponse?.resource;
  const pricesList = usedResourcePricesResponse?.usedResourcePriceList;
  const resourceUnit = usedResource?.type.unit ?? '-';

  useEffect(() => {
    if (usedResourceId) {
      setSelectedTableItems([]);
    }
  }, [usedResourceId]);

  const columns: ColumnsType<IUsedResourcePrice> = [
    {
      title: t('UsedResourceDetails.Table.CompanyName', 'Company name'),
      dataIndex: 'companyName',
    },
    {
      title: t('UsedResourceDetails.Table.UsedThisSeason', {
        defaultValue: 'Used this season ({{resourceUnit}})',
        resourceUnit,
      }),
      dataIndex: 'amountUsed',
      render: (amountUsed: number | null) =>
        amountUsed ? toFixedIfFloat(amountUsed) : '-',
    },
    {
      title: t('UsedResourceDetails.Table.AveragePrice', {
        defaultValue: 'Average price (€/{{resourceUnit}})',
        resourceUnit,
      }),
      dataIndex: 'averagePrice',
      render: (averagePrice: number | null) =>
        averagePrice ? toFixedIfFloat(averagePrice) : '-',
    },
    {
      title: t('UsedResourceDetails.Table.Sum', 'Sum'),
      dataIndex: 'sum',
      render: (_: unknown, priceItem) => {
        const { averagePrice, amountUsed } = priceItem;

        if (averagePrice && amountUsed) {
          const result = averagePrice * amountUsed;
          return toFixedIfFloat(result);
        }
        return '-';
      },
    },
  ];

  const rowSelection: TableRowSelection<IUsedResourcePrice> = {
    type: 'checkbox',
    selectedRowKeys: selectedTableItems.map(item => item.companyId),
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedTableItems(selectedRows);
    },
  };

  const onChangePriceSubmit = async (averagePrice: number) => {
    try {
      if (envId && pricesList && seasonId) {
        const usedResourcePriceCreateViewList: IUsedResourcePriceCreateViewItem[] = selectedTableItems.map(
          tableItem => {
            const { usedResourcePriceId, ...rest } = tableItem;
            return {
              ...rest,
              averagePrice,
              id: usedResourcePriceId,
              seasonId,
              hiikoEnvironmentId: envId,
            };
          },
        );

        await addUsedResourcePrice({
          envId,
          usedResourcePriceCreateViewList,
        }).unwrap();

        setIsPriceDrawerOpen(false);
        setSelectedTableItems([]);

        notification.success({
          message: t(
            'UsedResourceDetails.PriceChangeSuccess',
            'Average price paid changed successfully',
          ),
          placement: 'bottomRight',
        });
      }
    } catch (error) {
      dispatch(globalActions.handleError(error));
    }
  };

  return (
    <Card
      bordered={false}
      title={usedResource?.name}
      style={{
        maxHeight: 'calc(100vh - 163px)',
        overflow: 'auto',
        marginLeft: '12px',
      }}
    >
      <Descriptions bordered>
        <Descriptions.Item
          labelStyle={{ padding: '30px 24px', width: '197px' }}
          label={'Resurso aprašymas'}
        >
          {usedResource?.description ?? '-'}
        </Descriptions.Item>
      </Descriptions>

      <Descriptions
        bordered
        size="middle"
        column={4}
        labelStyle={{ width: '55px' }}
        style={{ marginTop: 20, width: '650px' }}
      >
        <Descriptions.Item label="N">
          {usedResource?.n ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item label="P">
          {usedResource?.p ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item label="K">
          {usedResource?.k ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item label="S">
          {usedResource?.s ?? '-'}
        </Descriptions.Item>
      </Descriptions>
      <Row style={{ margin: '38px 0 13px 0' }} justify="end">
        <Dropdown
          trigger={['click']}
          disabled={!selectedTableItems.length}
          overlay={
            <Menu>
              <Menu.Item
                onClick={() => {
                  setIsPriceDrawerOpen(true);
                }}
              >
                {t(
                  'UsedResourceDetails.ChangeAveragePrice',
                  'Change average price',
                )}
              </Menu.Item>
            </Menu>
          }
        >
          <Button
            type="primary"
            shape="circle"
            icon={<EllipsisOutlined />}
            size="small"
          />
        </Dropdown>
      </Row>

      <StyledTable
        rowKey="companyId"
        dataSource={pricesList}
        columns={columns}
        rowSelection={rowSelection}
        loading={isFetching}
      />

      <UsedResourcePriceDrawer
        isOpen={isPriceDrawerOpen}
        onClose={() => setIsPriceDrawerOpen(false)}
        onSubmit={onChangePriceSubmit}
        isSubmitLoading={isSubmitChangePriceLoading}
        resourceUnit={resourceUnit}
      />
    </Card>
  );
};

export default UsedResourceDetails;
